import { ContentFooter } from "@app/components/core/ContentFooter/ContentFooter"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import { MultilineText } from "@app/components/core/MultilineText/MultilineText"
import { SelectionEntry } from "@app/components/core/SelectionEntry/SelectionEntry"
import { SelectionGroup } from "@app/components/core/SelectionGroup/SelectionGroup"
import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import { BluphoriaSelector } from "@app/components/order/BluphoriaSelector/BluphoriaSelector"
import OrderContentBody from "@app/components/order/OrderContentBody/OrderContentBody"
import OrderSubPage from "@app/components/pageTypes/OrderSubPage/OrderSubPage"
import { OrderAnimationTypes } from "@app/components/routing/AnimatedOrderRoute/AnimatedOrderRoute"
import { addProduct, deleteOrderLine } from "@app/store/actions/orderActions"
import { useGetBluphoriaProducts, useGetProducts, useGetStrings } from "@app/store/service/cmsService"
import { useDelayLoading } from "@app/utils/hooks/useDelayLoading"
import { RenderHTML } from "@app/utils/htmlUtils"
import { selectFullPrice } from "@app/utils/selectors"
import tracking from "@app/utils/tracking"
import { useSelector } from "@tecreation/js-state-react"
import { useCallback, useEffect, useState } from "react"
import Skeleton from "react-loading-skeleton"

export default function BluphoriaSelection() {
	const [mappedArticles, setMappedArticles] = useState(undefined)

	const price = useSelector(selectFullPrice)
	const selectedProducts = useSelector(state => state.order.products)
	const { push, pop } = useCustomNavigation()

	const { data: bluphoriaData, isFetching: __loading } = useGetBluphoriaProducts()
	const { data: baseArticles, isLoading: articleLoading } = useGetProducts()
	const {
		data: { bluphoriaSelectionTitle, bluphoriaSelectionSubTitle, ourRecommendation, buttonShoppingCart },
	} = useGetStrings()

	const loading = useDelayLoading(__loading || articleLoading)

	useEffect(() => {
		if (bluphoriaData && baseArticles) {
			let mapped = [...bluphoriaData]
			mapped.forEach((e, i, a) => {
				a[i] = {
					...a[i],
				}
				let cnt = a[i].articles.length
				while (cnt--) {
					let article = a[i].articles[cnt]
					a[i].articles = [...a[i].articles]
					let found = baseArticles.find(a => a.articleId === article.articleId)
					if (found) {
						a[i].articles[cnt] = {
							...found,
						}
					} else {
						a[i].articles.splice(cnt, 1)
					}
				}
			})

			setMappedArticles(mapped)
		}
	}, [bluphoriaData, baseArticles])

	const next = useCallback(() => {
		let cnt = Object.keys(selectedProducts).reduce((v, o) => {
			return v + selectedProducts[o].cnt
		}, 0)
		tracking.bluphoriaConfirm(cnt)
		push("/order/order-overview", OrderAnimationTypes.fullSlide)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedProducts])

	return (
		<OrderSubPage>
			<ContentHeader
				canGoBack
				colorStyle="white"
				animation={OrderAnimationTypes.fullSlide}
				customReturnFunction={animation => {
					pop(-1, animation)
					// deleteOrderLines()
				}}
			>
				{bluphoriaSelectionTitle} <br /> <span className="f4 f-first">{bluphoriaSelectionSubTitle}</span>
			</ContentHeader>
			<OrderContentBody
				routeStyle={{
					height: "100%",
					background:
						"linear-gradient(#eaf1f5 30%, rgba(255, 255, 255, 0)), linear-gradient(rgba(255, 255, 255, 0), #eaf1f5 70%) 0 100%, linear-gradient(rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0)), linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.05)) 0 100%",
				}}
				stlye={{
					minHeight: "100%",
				}}
			>
				{/* While loading show empty selection entries */}
				{loading && (
					<SelectionGroup
						title={
							<>
								<span className="uppercase">
									<Skeleton />
								</span>
							</>
						}
					>
						<SelectionEntry isSkeleton />
						<SelectionEntry isSkeleton />
						<SelectionEntry isSkeleton />
						<SelectionEntry isSkeleton />
						<SelectionEntry isSkeleton />
						<SelectionEntry isSkeleton />
						<SelectionEntry isSkeleton />
					</SelectionGroup>
				)}
				{!loading && (!mappedArticles || mappedArticles.length == 0) && <div>no articles found...</div>}
				{!loading &&
					mappedArticles &&
					mappedArticles.map((e, index) => {
						return (
							<SelectionGroup
								key={index}
								title={
									<>
										<span className="uppercase">{e.title}</span>
										{e.isRecommended && (
											<>
												<span> - </span>
												<span className="color-fg-success">{ourRecommendation}</span>
											</>
										)}
									</>
								}
							>
								{e.articles.map(article => {
									return (
										<BluphoriaSelector
											key={article.articleId}
											count={selectedProducts[article.articleId] ? selectedProducts[article.articleId].cnt : 0}
											imgSrc={article.gallery[0] ? article.gallery[0].directus_files_id : ""}
											name={article.title}
											price={article.price}
											volume={article.weight}
											basePrice={article.basePrice}
											entity={article.entity?.short}
											baseEntity={article.baseEntity?.short}
											onAdd={() => addProduct(article)}
											onRemove={() => deleteOrderLine(article)}
											expanded={
												article.description ? (
													<div className="px-2 py-4">
														<MultilineText>
															<RenderHTML html={article.description} />
														</MultilineText>
													</div>
												) : undefined
											}
										/>
									)
								})}
							</SelectionGroup>
						)
					})}
			</OrderContentBody>
			<ContentFooter process={4} price={price} priceAbsolute buttonProps={{ text: buttonShoppingCart, onNext: next }} />
		</OrderSubPage>
	)
}
